import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, Grid, TextField, InputAdornment, Button, Divider, Chip, IconButton, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getInvoiceByOrderId } from "../../service/Invoice/invoice";
import { useCallback } from "react";
import { getCompletedOrders, getInvoiceDownload } from '../../service/order/orderService';
import { useParams } from "react-router-dom";
import { notifyError, notifySuccess } from "../../components/ToastContainer/Toast/Toast";
import vegIcon from "../../Assets/Veg_symbol.svg.png"
import nonVegIcon from "../../Assets/Non_veg_symbol.svg.png"
import eggIcon from "../../Assets/Egg.svg"
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { Product } from "./components/ProductSummary";
import dayjs, { Dayjs } from "dayjs";
import {
  isInvoiceRefundCompleted,
  isInvoicePaidChipPositive,
  shouldShowCancelledInvoiceRefundChip,
} from "./utils/orderHistory.helperFunction";
export interface InvoiceItem {
    product_name: string;
    quantity: number;
    price: number;
    veg: boolean;
}

export interface InvoiceCustomer {
    id: number;
    name: string;
    email: string;
    phone: string;
    billing_address: string;
}

export interface Invoice {
    id: number;
    order_id: string;
    invoice_date: string;
    status: string;
    customer: InvoiceCustomer;
    customer_name?: string;
    items: InvoiceItem[];
    total_amount: number;
    timeline?: { label: string; time: string }[];
}

interface Order {
    id: number;
    time: string;
    date: string;
    customer: string;
    items: { name: string; qty: number; price: number; veg: boolean; }[];
    total: number;
    status: string;
    rating: number;
    timeline: { label: string; time: string; }[];
    cancelReason?: string;
    invoice_date?: string;
}

const OrdersHistory: React.FC = () => {
    const [invoiceList, setInvoiceList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [invoiceId, setInvoiceId] = useState(null);
    const [invoiceDetail, setInvoiceDetail] = useState<any | null>(null);
    const [search, setSearch] = useState("");
    const { store_id } = useParams();
    const [selectedStatus, setSelectedStatus] = useState<"all" | "completed" | "cancelled">("all");
    const [orderId, setOrderId] = useState<number | null>(null);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [filter, setFilter] = useState<"today" | "this_week" | "this_month">("this_month");
    const minSelectableDate = dayjs().subtract(3, "month");
    const maxSelectableDate = dayjs();
    
    // Fetch paginated completed orders
    const fetchOrders = useCallback(async (pageToFetch = 1, append = false) => {
        if (pageToFetch === 1) setLoading(true);
        else setFetchingMore(true);
        let fromDateParam: string | undefined;
        let toDateParam: string | undefined;
        if (fromDate && toDate) {
            fromDateParam = fromDate.format("YYYY-MM-DD");
            toDateParam = toDate.format("YYYY-MM-DD");
        } else {
            const now = dayjs();
            if (filter === "today") {
                fromDateParam = now.startOf("day").format("YYYY-MM-DD");
                toDateParam = now.endOf("day").format("YYYY-MM-DD");
            } else if (filter === "this_week") {
                fromDateParam = now.startOf("week").format("YYYY-MM-DD");
                toDateParam = now.endOf("week").format("YYYY-MM-DD");
            } else {
                fromDateParam = now.startOf("month").format("YYYY-MM-DD");
                toDateParam = now.endOf("month").format("YYYY-MM-DD");
            }
        }

        try {
            const res = await getCompletedOrders(pageToFetch, store_id,
                selectedStatus.toLowerCase(), fromDateParam, toDateParam
            );
            const data = res?.data?.data;
            const orders = data?.orders || [];
            const newTotalPages = data?.totalPages || 1;
            setTotalPages(newTotalPages);
            if (append) {
                setInvoiceList(prev => [...prev, ...(Array.isArray(orders) ? orders : [])]);
            } else {
                setInvoiceList(Array.isArray(orders) ? orders : []);
            }
        } catch (e) {
            if (!append) setInvoiceList([]);
        } finally {
            if (pageToFetch === 1) setLoading(false);
            else setFetchingMore(false);
        }
    }, [selectedStatus, fromDate, toDate, store_id, filter]);

    useEffect(() => {
        if ((fromDate && !toDate) || (!fromDate && toDate)) {
            return;
        }
        setPage(1);
        fetchOrders(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, fromDate, toDate, filter, store_id]);

    // Only show delivered/cancelled in UI (API should already filter)
    const filteredList = invoiceList.filter((ord) => ord.status === "delivered" || ord.status === "cancelled");

    // Details logic unchanged
    const fetchInvoiceById = async (id: number) => {
        try {
            const res = await getInvoiceByOrderId(id);
            const data = res?.data?.data || null;

            if (res?.data?.success === false) {
                notifyError(res?.data?.message || "Failed to fetch invoice");
                setInvoiceDetail(null);
                setInvoiceId(null);
                return;
            }
            setInvoiceDetail(data);
            setInvoiceId(id);
        } catch (err: any) {
            setInvoiceDetail(null);
        }
    };

    // Filtering logic — search only; status is already filtered by API
    const filteredInvoices = invoiceList.filter((order) => {
        const matchesSearch =
            order.id.toString().toLowerCase().includes(search.toLowerCase()) ||
            order.order_code?.toString().toLowerCase().includes(search.toLowerCase()) ||
            order.customer?.name?.toLowerCase().includes(search.toLowerCase());
        return matchesSearch;
    });

    const groupedDisplayItems = useMemo(() => {
        const baseInvoiceMap = new Map<string, any & { addons?: any[] }>()
        
        invoiceDetail?.forEach((invoice: any) => {
            // First pass: Process all base products (non-addon items)
            invoice.items.forEach((item: any) => {
                if (!String(item.product_id).startsWith("a-")) {
                    if (!baseInvoiceMap.has(item.product_id)) {
                        baseInvoiceMap.set(item.product_id, { ...item, addons: [] });
                    }
                }
            });

            // Second pass: Process addons and attach them to their base products
            invoice.items.forEach((item: any) => {
                if (String(item.product_id).startsWith("a-")) {
                    const match = String(item.product_id).match(/a-(\d+)(?:-\d+)?/);
                    const baseId = match ? match[1] : null;
                    
                    if (baseId && baseInvoiceMap.has(baseId)) {
                        const baseItem = baseInvoiceMap.get(baseId);
                        if (!baseItem.addons) {
                            baseItem.addons = [];
                        }
                        baseItem.addons.push(item);
                    } else {
                        // If no corresponding base product is found, treat as standalone
                        baseInvoiceMap.set(item.product_id, { ...item, addons: [] });
                    }
                }
            });
        });
        
        return Array.from(baseInvoiceMap.values());
    }, [invoiceDetail]);

    const handleFetchInvoice = (id) => {
        setOrderId(id);
        fetchInvoiceById(id);
    };

    const handleChipClick = (status) => {
        setSelectedStatus(status);
        setOrderId(null);
        setInvoiceDetail(null);
    };

    const handleFilterClick = (newFilter: "today" | "this_week" | "this_month") => {
        setFilter(newFilter);
        setFromDate(null);
        setToDate(null);
        setOrderId(null);
        setInvoiceDetail(null);
    };

    const handleResetFilters = () => {
        setFilter("this_month");
        setFromDate(null);
        setToDate(null);
        setOrderId(null);
        setInvoiceDetail(null);
    };

    const downloadInvoice = async (id: number) => {
        try {
            const res = await getInvoiceDownload(id); // make sure response is a blob
            if (res) {
                // Create a Blob from the response
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                // Create a temporary link and trigger download
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoice_${id}.pdf`); // set file name
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);

                notifySuccess("Invoice downloaded successfully");
            }
        } catch (error: any) {
            notifyError(error?.data?.message || "Download failed");
        }
    };


    return (
        <Box>
            {/* Top Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 1.5,
                    mb: 1,
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={1.5}>
                    <Typography variant="h6" fontWeight="bold">
                        Order History
                    </Typography>
                    {/* Preset Filter Buttons + Date Pickers */}
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        {/* Preset Buttons */}
                        {(["today", "this_week", "this_month"] as const).map((f, i) => {
                            const labels = ["Today", "This Week", "This Month"];
                            const isActive = filter === f && !fromDate && !toDate;
                            return (
                                <Button
                                    key={f}
                                    onClick={() => handleFilterClick(f)}
                                    variant={isActive ? "contained" : "outlined"}
                                    sx={{
                                        backgroundColor: isActive ? '#000000' : '#ffffff',
                                        color: isActive ? '#ffffff' : '#000000',
                                        border: isActive ? 'none' : '1px solid #e5e7eb',
                                        fontWeight: isActive ? 600 : 400,
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: 12,
                                        minWidth: 'unset',
                                        '&:hover': {
                                            backgroundColor: isActive ? '#000000' : '#f9fafb',
                                            borderColor: isActive ? 'transparent' : '#e5e7eb',
                                        },
                                        boxShadow: 'none',
                                    }}
                                >
                                    {labels[i]}
                                </Button>
                            );
                        })}

                        {/* Date Pickers */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => {
                                    setFromDate(newValue);
                                    setOrderId(null);
                                    setInvoiceDetail(null);
                                    if (newValue && toDate && toDate.isBefore(newValue, "day")) {
                                        setToDate(null);
                                    }
                                }}
                                minDate={minSelectableDate}
                                maxDate={maxSelectableDate}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { width: { xs: "100%", sm: 140 }, '& .MuiOutlinedInput-root': { fontSize: 12 } },
                                    },
                                    openPickerButton: {
                                        sx: { border: "none", boxShadow: "none", "&:hover": { backgroundColor: "transparent" } },
                                    },
                                }}
                                format="YYYY-MM-DD"
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => {
                                    setToDate(newValue);
                                    setOrderId(null);
                                    setInvoiceDetail(null);
                                }}
                                minDate={fromDate || minSelectableDate}
                                maxDate={maxSelectableDate}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { width: { xs: "100%", sm: 140 }, '& .MuiOutlinedInput-root': { fontSize: 12 } },
                                    },
                                    openPickerButton: {
                                        sx: { border: "none", boxShadow: "none", "&:hover": { backgroundColor: "transparent" } },
                                    },
                                }}
                                format="YYYY-MM-DD"
                            />
                        </LocalizationProvider>

                        {/* Reset Button */}
                        <IconButton
                            aria-label="Reset filters"
                            size="small"
                            onClick={handleResetFilters}
                            sx={{ border: "1px solid #e0e0e0", borderRadius: 1, width: 32, height: 32 }}
                        >
                            <RestartAltOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Divider />

            <Grid container> 
                {/* Left Side: Search and Orders List */}
                <Grid item xs={12} md={4} sx={{ pt: 2, pr: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search by ID, Name"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ bgcolor: '#f8f9fa', mb: 2 }}
                        />
                    <Divider />

                    {/* Scrollable Order List */}
                    <Box>
                        {/* Chips Row */}
                        <Box display="flex" flexWrap="wrap" gap={1} mt={2} mb={2}>
                            {[{label: "All", value: "all"}, {label: "Delivered", value: "completed"}, {label: "Cancelled", value: "cancelled"}].map((status) => (
                                <Chip
                                    label={status.label}
                                    sx={{ p: 1.5 }}
                                    color={selectedStatus === status.value ? "primary" : "default"}
                                    onClick={() => handleChipClick(status.value.toLowerCase())}
                                />
                            ))}
                        </Box>

                        {/* Invoice List Box */}
                        <Box
                            sx={{
                                mt: 2,
                                maxHeight: "calc(100vh - 320px)",
                                overflowY: "auto",
                                "&::-webkit-scrollbar": { display: "none" },
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                            }}
                        >
                            {filteredInvoices.map((order, index) => {
                                const dateObj = new Date(order.order_date);
                                const date = dateObj.toLocaleDateString("en-IN");
                                const time = dateObj.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

                                return (
                                    <Paper
                                        key={index}
                                        onClick={() => handleFetchInvoice(order.id)}
                                        sx={{
                                            mb: 1.5,
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            bgcolor: "background.paper",
                                            border: "1px solid #ddd",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                bgcolor: order.status === "cancelled" ? "#6c757d" : "black",
                                                color: "white",
                                                px: 2,
                                                py: 1,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                textAlign: "center",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {order.status}
                                        </Typography>
                                        <Box p={1.5}>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography fontWeight="bold" variant="body2" width={'50%'} sx={{textWrap: 'wrap', whiteSpace: 'normal',}}>Order ID : {order.order_code}</Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    By {order.customer?.name}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" my={1}>
                                                <Typography fontWeight="bold" variant="body2" color="text.primary">
                                                    Store Name:
                                                </Typography>
                                                <Typography fontWeight="bold" variant="body2" color="text.primary">
                                                    {order.store.name}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mt={0.5} pb={1}>
                                                <Typography variant="body2" color="text.secondary">{time}</Typography>
                                                <Typography variant="body2" color="text.secondary">{date}</Typography>
                                            </Box>
                                            {/* <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                sx={{ backgroundColor: "white", px: 1.5, py: 1, mx: -1.5, mb: -1.5 }}
                                            >
                                                <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: "70%" }}>
                                                    {order.order_items.map(i => `${i.product_name} x ${i.quantity}`).join(", ")}
                                                </Typography>
                                                <Typography fontWeight="600" fontSize={12}>
                                                    ₹{order.order_items.reduce((acc, i) => acc + Number(i.price) * i.quantity, 0).toFixed(2)}
                                                </Typography>
                                            </Box> */}
                                        </Box>
                                    </Paper>
                                );
                            })}
                            {page < totalPages && (
                                <Box display="flex" justifyContent="center" py={2}>
                                    {fetchingMore ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddCircleOutlineIcon />}
                                            onClick={() => {
                                                const nextPage = page + 1;
                                                setPage(nextPage);
                                                fetchOrders(nextPage, true);
                                            }}
                                        >
                                            View More
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
                <Divider />

                {/* Right Side: Order Details */}
                <Grid item xs={12} md={8} sx={{ borderLeft: '1px solid #eee' }}>
                    {/* Order Details Content */}
                    {invoiceDetail && invoiceDetail.length > 0 ? (
                        invoiceDetail.map((invoice, idx) => (
                            invoice.status === 'cancelled' ? (
                                <>
                                    <Box key={idx} sx={{ p: 2, mb: 3 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography fontSize={'16px'} fontWeight={500}>
                                                Order Details
                                            </Typography>
                                            <Box display="flex" gap={1}>
                                                {/* <Button
                                                    onClick={() => downloadInvoice(invoice.id)}
                                                    variant="contained"
                                                    startIcon={<DownloadIcon />}
                                                    size="small"
                                                    sx={{ textTransform: "none", bgcolor: "#343a40", color: "white" }}
                                                >
                                                    Invoice
                                                </Button> */}
                                            </Box>
                                        </Box>

                                        <Divider sx={{ width: '100%', my: 2 }} />

                                        <Box display="flex" justifyContent="space-between">
                                            <Box>
                                                <Typography fontWeight="bold" fontSize={14}>ID : {invoice.id}</Typography>
                                                <Box display="flex" gap={1} mt={0.5}>
                                                  <Chip label="Cancelled" size="small" sx={{ bgcolor: '#ffb2ae', color: '#922b26', fontWeight: 500, fontSize: 10 }} />
                                                  {shouldShowCancelledInvoiceRefundChip(invoice) && (
                                                    <Chip 
                                                      label={isInvoiceRefundCompleted(invoice) ? "Refunded" : "Refund Pending"} 
                                                      size="small" 
                                                      sx={{ 
                                                        bgcolor: isInvoiceRefundCompleted(invoice) ? '#d4edda' : '#fff3cd',
                                                        color: isInvoiceRefundCompleted(invoice) ? '#155724' : '#856404',
                                                        fontWeight: 500, 
                                                        fontSize: 10 
                                                      }} 
                                                    />
                                                  )}
                                                </Box>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography variant="body2" fontSize={12} fontWeight="500">By {invoice.customer?.name}</Typography>
                                                <Typography variant="body2" fontSize={12} color="text.secondary">
                                                    {new Date(invoice.invoice_date).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        {invoice.cancel_reason && (
                                            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body1" fontSize={12}>Cancel Reason</Typography>
                                                <Typography variant="body1" fontSize={12} fontWeight={500}>{invoice.cancel_reason.reason}</Typography>
                                            </Box>
                                        )}
                                        <Divider sx={{ my: 2 }} />
                                        <Typography fontSize={14} fontWeight={500} mb={1.5}>Summary</Typography>
                                        <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {(() => {
                                                // Apply the same grouping logic for cancelled orders
                                                const baseInvoiceMap = new Map<string, any & { addons?: any[] }>();
                                                
                                                // First pass: Process all base products (non-addon items)
                                                invoice.items.forEach((item: any) => {
                                                    if (!String(item.product_id).startsWith("a-")) {
                                                        if (!baseInvoiceMap.has(item.product_id)) {
                                                            baseInvoiceMap.set(item.product_id, { ...item, addons: [] });
                                                        }
                                                    }
                                                });

                                                // Second pass: Process addons and attach them to their base products
                                                invoice.items.forEach((item: any) => {
                                                    if (String(item.product_id).startsWith("a-")) {
                                                        const match = String(item.product_id).match(/a-(\d+)(?:-\d+)?/);
                                                        const baseId = match ? match[1] : null;
                                                        
                                                        if (baseId && baseInvoiceMap.has(baseId)) {
                                                            const baseItem = baseInvoiceMap.get(baseId);
                                                            if (!baseItem.addons) {
                                                                baseItem.addons = [];
                                                            }
                                                            baseItem.addons.push(item);
                                                        } else {
                                                            // If no corresponding base product is found, treat as standalone
                                                            baseInvoiceMap.set(item.product_id, { ...item, addons: [] });
                                                        }
                                                    }
                                                });

                                                const groupedItems = Array.from(baseInvoiceMap.values());

                                                return groupedItems.length > 0 ? (
                                                    groupedItems.map((item, i) => (

                                                        <Product.Summary item={item} key={i} />

                                                    ))
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">No items available</Typography>
                                                );
                                            })()}
                                        </Box>

                                        <Divider />
                                        <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>  
                                            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                                <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">  Sub Total: </Typography>
                                                <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">
                                                    ₹{invoice.sub_total?.toFixed(2) || "0.00"}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider />

                                        <Divider sx={{ my: 1.5 }} />
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body2" fontSize={10} color="text.secondary">Taxes</Typography>
                                            <Typography variant="body2" fontSize={12}>{invoice?.tax_amount?.toFixed(2) || "0.00"}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body2" fontSize={10} color="text.secondary">Discounts</Typography>
                                            <Typography variant="body2" fontSize={12}>{invoice?.discount_amount?.toFixed(2) || "0.00"}</Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Typography fontWeight={500} fontSize={12}>Total</Typography>
                                                 <Chip label={isInvoicePaidChipPositive(invoice) ? "Paid" : "Not Paid"} size="small"
                                                                        sx={{ bgcolor: isInvoicePaidChipPositive(invoice)
                                                                              ? "#28a745" : "#ffa500", "& .MuiChip-label": {
                                                                            color: "#fff", fontWeight: 500, fontSize: 10
                                                                          }
                                                                        }}
                                                 />
                                            </Box>
                                            <Typography fontWeight={600} fontSize={12}>₹{invoice.total_amount.toFixed(2)}</Typography>
                                        </Box>
                                    </Box>
                                </>
                            ) : (
                                <Box key={idx}
                                    sx={{
                                        p: 2, mb: 3, maxHeight: '700px', overflowY: 'auto', bgcolor: '#fff',
                                        '&::-webkit-scrollbar': {
                                            display: 'none',
                                        },
                                        msOverflowStyle: 'none',
                                        scrollbarWidth: 'none',
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" >
                                        <Typography fontSize={'16px'} fontWeight={500}>
                                            Order Details
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                            {/* <Button
                                                onClick={() => downloadInvoice(invoice.id)}
                                                variant="contained"
                                                startIcon={<DownloadIcon />}
                                                size="small"
                                                sx={{ textTransform: "none", bgcolor: "#343a40", color: "white" }}
                                            >
                                                Invoice
                                            </Button> */}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ width: '100%', my: 2 }} />

                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Box>
                                            <Typography fontWeight="bold" fontSize={12}>Invoice ID : {invoice.id}</Typography>
                                            <Typography variant="body2" fontSize={12}>
                                                {new Date(invoice.invoice_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </Typography>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="body2" fontSize={12} fontWeight="500">By {invoice.customer?.name}</Typography>
                                            <Typography variant="body2" fontSize={12} color="text.secondary">
                                                {new Date(invoice.invoice_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    {/* Customer Rating */}
                                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body1" fontSize={12}>Customer Rating</Typography>
                                        <Box>
                                            {invoice.customer_ratings ? (
                                                [...Array(5)].map((_, i) => (
                                                    <StarIcon key={i} sx={{ color: i < invoice.customer_ratings ? "#ffc107" : "#ddd", fontSize: 20 }} />
                                                ))
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">No Rating</Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    {invoice.timeline && invoice.timeline.length > 0 && (
                                        (() => {
                                            // Define the correct order flow
                                            const statusOrder = [
                                                "pending",
                                                "billed", 
                                                "packed",
                                                "picked",
                                                "agent-assigned",
                                                "out-for-delivery",
                                                "delivered"
                                            ];

                                            // Filter timeline entries
                                            const filteredTimeline = invoice.timeline
                                                .filter((t) => {
                                                    // Include order statuses and invoice statuses
                                                    if (t.type === "order") {
                                                        return ["pending", "shipped", "completed"].includes(t.status);
                                                    }
                                                    if (t.type === "invoice") {
                                                        return ["billed", "packed", "picked", "agent-assigned", "out-for-delivery", "delivered"].includes(t.status);
                                                    }
                                                    return false;
                                                })
                                                .map((t) => ({
                                                    ...t,
                                                    displayStatus: t.status === "shipped" ? "out-for-delivery" : 
                                                                 t.status === "completed" ? "delivered" : t.status
                                                }));

                                            // Remove duplicates and get the latest entry for each status
                                            const uniqueTimeline = statusOrder
                                                .map(status => {
                                                    const entries = filteredTimeline.filter(t => t.displayStatus === status);
                                                    if (entries.length > 0) {
                                                        // Get the latest entry for this status
                                                        return entries.sort((a, b) => 
                                                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                                                        )[0];
                                                    }
                                                    return null;
                                                })
                                                .filter(Boolean); // Remove null entries

                                            // Sort by timestamp (date + time) so timeline displays in chronological order
                                            const sortedTimeline = [...uniqueTimeline].sort(
                                                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                                            );

                                            return (
                                                sortedTimeline.length > 0 && (
                                                    <>
                                                        <Typography fontSize={14} fontWeight={500} mb={2}>
                                                            Order Timeline
                                                        </Typography>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={2}>
                                                            {sortedTimeline.map((t, i) => {
                                                                return (
                                                                    <React.Fragment key={i}>
                                                                        <Box textAlign="center" sx={{ minWidth: 60 }}>
                                                                            <CheckCircleIcon sx={{ color: "green", fontSize: 22 }} />
                                                                            <Typography fontSize={10} color="text.secondary" sx={{ textTransform: "capitalize" }}>
                                                                                {t.displayStatus.replace(/-/g, " ")}
                                                                            </Typography>
                                                                            <Typography fontSize={12}>
                                                                                {new Date(t.created_at).toLocaleTimeString([], {
                                                                                    hour: "2-digit",
                                                                                    minute: "2-digit",
                                                                                })}
                                                                            </Typography>
                                                                        </Box>
                                                                        {i < sortedTimeline.length - 1 && (
                                                                            <Box
                                                                                flexGrow={1}
                                                                                sx={{
                                                                                    borderBottom: "2px dotted green",
                                                                                    transform: "translateY(-14px)",
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                )
                                                            })}
                                                        </Box>
                                                        <Divider sx={{ my: 2 }} />
                                                    </>
                                                )
                                            );
                                        })()
                                    )}


                                    {/* Items Summary */}
                                    <Typography fontSize={14} fontWeight={500} mb={1.5}>Summary</Typography>
                                    <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {groupedDisplayItems && groupedDisplayItems.length > 0 ? (
                                            groupedDisplayItems.map((item, i) => (

                                                <Product.Summary item={item} key={i} />
                                                
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">No items available</Typography>
                                        )}
                                    </Box>

                                    <Divider />
                                    
                                    <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>  
                                        <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                            <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">  Sub Total: </Typography>
                                            <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">
                                                ₹{invoice.sub_total?.toFixed(2) || "0.00"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1.5 }} />
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" fontSize={10} color="text.secondary">Taxes</Typography>
                                        <Typography variant="body2" fontSize={12}>{invoice?.tax_amount?.toFixed(2) || "0.00"}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" fontSize={10} color="text.secondary">Discounts</Typography>
                                        <Typography variant="body2" fontSize={12}>{invoice?.discount_amount?.toFixed(2) || "0.00"}</Typography>
                                    </Box>

                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography fontWeight={500} fontSize={12}>Total</Typography>
                                            <Chip label="Paid" size="small" sx={{ bgcolor: '#28a745', "& .MuiChip-label": { color: "white", fontWeight: 500, fontSize: 10 } }} />
                                        </Box>
                                        <Typography fontWeight={600} fontSize={12}>₹{invoice.total_amount?.toFixed(2) || "0.00"}</Typography>
                                    </Box>
                                </Box>
                            )
                        ))
                    ) : (
                        <Paper sx={{ m: 2, p: 4, textAlign: "center", borderRadius: 2 }}>
                            <Typography color="text.secondary">Please select an Order from the list.</Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default OrdersHistory;